import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import useFlag from 'app/hooks/useFlag';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import { useMemo, useContext } from 'react';
import { sharesApi } from 'services/api/shares';
import { SharedItemsResponse } from 'services/api/shares/types';
import { ISharedEquipmentEntry, ISharedUser } from 'types/IEquipmentEntry';
import { FeatureName } from 'types/entities/MobileSettings';
import { groupSharedUsers } from 'utils/shares';

const useGetEquipmentData = (
  equipmentId?: string,
): {
  equipments?: ISharedEquipmentEntry[];
  isLoading: boolean;
  sharesData?: SharedItemsResponse;
  equipmentList?: ISharedEquipmentEntry[];
  selectedEquipment?: ISharedEquipmentEntry;
  sharedUsers?: ISharedUser[];
  refetch: () => void;
} => {
  const isAccessSharingPageEnabled = useFlag(FeatureName.ACCESS_SHARING);

  const {
    data: sharesData,
    isLoading,
    refetch,
  } = sharesApi.useGetSharesQuery(undefined, {
    skip: !isAccessSharingPageEnabled,
  });

  const {
    equipmentListData: { equipmentList },
  } = useContext(EquipmentListContext);

  const { data: user } = useGetUserInfoQuery();

  const equipments = useMemo(() => {
    if (!equipmentList || isLoading) return [];
    return equipmentList.map(equipment => {
      const equipmentSharesData = sharesData?.equipment.find(
        sharedEquipment =>
          sharedEquipment.sharedItemDetails.exposedId === equipment.id,
      );

      const equipmentFleetData = sharesData?.fleets.find(fleet =>
        fleet.sharedItemDetails.exposedEquipmentIds?.includes(equipment.id),
      );

      return {
        ...equipment,
        isSharedIndividually: !!equipmentSharesData,
        isSharedAsPartOfFleet: !!equipmentFleetData,
        shareData: equipmentSharesData,
        fleetData: equipmentFleetData,
      };
    });
  }, [equipmentList, sharesData]);

  const sharedUsers = useMemo(() => {
    if (!user || !equipments || isLoading) return [];
    return groupSharedUsers(equipments, user);
  }, [equipments, user]);

  const selectedEquipment = useMemo(() => {
    if (!equipmentId || !equipments || isLoading) return;

    return equipments.find(equipment => equipment.id === equipmentId);
  }, [equipments, equipmentId]);
  return {
    sharedUsers,
    equipments,
    isLoading,
    sharesData,
    selectedEquipment,
    refetch,
  };
};

export default useGetEquipmentData;
